import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Preview() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/preview/start", {
        email: email.trim(),
        name: name.trim() || null,
        source: "preview_landing",
      });
      const token = res.data.token;
      localStorage.setItem("sol_preview_token", token);
      toast.success("Module 1 unlocked. Check your email for the access link.");
      nav(`/preview/course?token=${token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start preview. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <SEO
        title="Free Preview — Module 1 of Forged in Fire"
        description="Get the first module of Forged in Fire: The Course free — four lessons, about ninety minutes. No card, no trial. Yours to keep."
      />
      <section className="border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="overline text-brand-rust">Free preview</span>
            <h1 className="font-display text-5xl md:text-6xl mt-4 text-brand-ink leading-[1.05]">
              The first module is yours.
            </h1>
            <p className="mt-6 text-lg text-brand-inkMuted max-w-2xl leading-relaxed">
              Module 1 of <em>Forged in Fire: The Course</em> — four lessons, about ninety minutes — delivered free in exchange for your email. No card. No trial. Yours to keep.
            </p>

            <ul className="mt-10 space-y-4 text-brand-ink">
              <li className="flex gap-4 items-start" data-testid="preview-bullet-1">
                <span className="text-brand-rust mt-1">—</span>
                <span><strong>Lesson 1:</strong> Pressure as a Teacher</span>
              </li>
              <li className="flex gap-4 items-start" data-testid="preview-bullet-2">
                <span className="text-brand-rust mt-1">—</span>
                <span><strong>Lesson 2:</strong> Why Most Leaders Meet Heat Unprepared</span>
              </li>
              <li className="flex gap-4 items-start" data-testid="preview-bullet-3">
                <span className="text-brand-rust mt-1">—</span>
                <span><strong>Lesson 3:</strong> Choosing Your Fire</span>
              </li>
              <li className="flex gap-4 items-start" data-testid="preview-bullet-4">
                <span className="text-brand-rust mt-1">—</span>
                <span><strong>Lesson 4:</strong> Naming the Work In Front of You</span>
              </li>
            </ul>

            <p className="mt-10 text-sm text-brand-inkMuted max-w-xl">
              Over the next week you&apos;ll receive four short notes — one every couple of days — each pulling a thread from the rest of the course. No pitch. Just the kind of leadership thinking I write for the people I respect most.
            </p>

            <div className="mt-12 aspect-video bg-black max-w-2xl" data-testid="preview-trailer">
              <iframe
                title="South on Leadership — Trailer"
                src="https://www.youtube.com/embed/0tQwsSac9D8"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          <div className="lg:col-span-5">
            <form
              onSubmit={onSubmit}
              data-testid="preview-form"
              className="bg-white border border-brand-line p-8 md:p-10"
            >
              <div className="overline text-brand-rust">Unlock Module 1</div>
              <h2 className="font-display text-3xl text-brand-ink mt-3 leading-tight">
                Where should I send it?
              </h2>

              <div className="mt-8 space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[.18em] text-brand-inkMuted" htmlFor="pv-name">Your name (optional)</label>
                  <input
                    id="pv-name"
                    data-testid="preview-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-2 w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust transition-colors"
                    placeholder="Robbie"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[.18em] text-brand-inkMuted" htmlFor="pv-email">Email</label>
                  <input
                    id="pv-email"
                    data-testid="preview-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust transition-colors"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="preview-submit"
                className="mt-10 w-full bg-brand-ink text-white hover:bg-black px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
              >
                {loading ? "Unlocking…" : "Send me Module 1 →"}
              </button>

              <p className="mt-5 text-xs text-brand-inkMuted leading-relaxed">
                One welcome email + four short follow-ups over seven days. Unsubscribe anytime. Your email is never shared.
              </p>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
